import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchFeeAccessStatus,
  unlockFeeSubsystem,
  setFeeSubsystemPassword,
  type SchoolCode,
} from '../api/axios';
import { FeeManagement } from './FeeManagement';

type FeeTab = 'structure' | 'history' | 'report';

interface FeeManagementGateProps {
  school: string;
  section: FeeTab;
}

export const FeeManagementGate: React.FC<FeeManagementGateProps> = ({ school, section }) => {
  const schoolCode = school as SchoolCode;
  const [loading, setLoading] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [hasFeesPassword, setHasFeesPassword] = useState(false);
  const [canUseMasterReset, setCanUseMasterReset] = useState(true);
  const [nextMasterResetAfter, setNextMasterResetAfter] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  const [feesPassword, setFeesPassword] = useState('');
  const [masterPassword, setMasterPassword] = useState('');
  const [newFeesPassword, setNewFeesPassword] = useState('');
  const [confirmFeesPassword, setConfirmFeesPassword] = useState('');
  const [showMasterFlow, setShowMasterFlow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setConfigError(null);
    setFormError(null);
    try {
      const s = await fetchFeeAccessStatus(schoolCode);
      setUnlocked(s.unlocked);
      setHasFeesPassword(s.has_fees_password);
      setCanUseMasterReset(s.can_use_master_reset);
      setNextMasterResetAfter(s.next_master_reset_after);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to load fee access status';
      setConfigError(msg);
      setUnlocked(false);
    } finally {
      setLoading(false);
    }
  }, [schoolCode]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setBusy(true);
    try {
      await unlockFeeSubsystem(schoolCode, feesPassword);
      setFeesPassword('');
      await loadStatus();
    } catch (err: any) {
      setFormError(err?.response?.data?.error || 'Unlock failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (newFeesPassword !== confirmFeesPassword) {
      setFormError('New passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await setFeeSubsystemPassword({
        school: schoolCode,
        master_password: masterPassword,
        new_fees_password: newFeesPassword,
      });
      setMasterPassword('');
      setNewFeesPassword('');
      setConfirmFeesPassword('');
      setShowMasterFlow(false);
      await loadStatus();
    } catch (err: any) {
      setFormError(err?.response?.data?.error || 'Could not set fees password');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>
        Checking fee management access…
      </div>
    );
  }

  if (configError) {
    return (
      <div
        style={{
          maxWidth: 520,
          margin: '40px auto',
          padding: 24,
          borderRadius: 12,
          border: '1px solid #fecaca',
          background: '#fef2f2',
          color: '#991b1b',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: 18 }}>Fee management unavailable</h2>
        <p style={{ marginBottom: 0, lineHeight: 1.5 }}>{configError}</p>
      </div>
    );
  }

  if (unlocked) {
    return <FeeManagement school={school} section={section} />;
  }

  return (
    <div
      style={{
        maxWidth: 480,
        margin: '32px auto',
        padding: 28,
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        background: '#fff',
        boxShadow: '0 4px 24px rgba(15,23,42,0.08)',
      }}
    >
      <h2 style={{ marginTop: 0, marginBottom: 8, fontSize: 20, color: '#0f172a' }}>
        Fee management — {school}
      </h2>
      <p style={{ marginTop: 0, marginBottom: 20, fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
        This area is restricted. Enter your <strong>fees password</strong> for this school. If you have not set one
        yet, or you forgot it, use the <strong>school master password</strong> to create a new fees password (master
        reset is limited to once per week after your first password is set).
      </p>

      {!showMasterFlow ? (
        <form onSubmit={handleUnlock}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
            Fees password
          </label>
          <input
            type="password"
            autoComplete="new-password"
            value={feesPassword}
            onChange={(e) => setFeesPassword(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              marginBottom: 16,
              fontSize: 15,
            }}
          />
          <button
            type="submit"
            disabled={busy || !feesPassword}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 8,
              border: 'none',
              background: '#1e3a5f',
              color: '#fff',
              fontWeight: 600,
              cursor: busy || !feesPassword ? 'not-allowed' : 'pointer',
              marginBottom: 12,
            }}
          >
            {busy ? 'Unlocking…' : 'Unlock fee management'}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowMasterFlow(true);
              setFormError(null);
            }}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              background: '#f8fafc',
              color: '#334155',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {hasFeesPassword ? 'Forgot password — use master password' : 'First time — set password with master'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleSetPassword}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
            Master password ({school})
          </label>
          <input
            type="password"
            value={masterPassword}
            onChange={(e) => setMasterPassword(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              marginBottom: 12,
              fontSize: 15,
            }}
          />
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
            New fees password (min 8 characters)
          </label>
          <input
            type="password"
            value={newFeesPassword}
            onChange={(e) => setNewFeesPassword(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              marginBottom: 12,
              fontSize: 15,
            }}
          />
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
            Confirm new fees password
          </label>
          <input
            type="password"
            value={confirmFeesPassword}
            onChange={(e) => setConfirmFeesPassword(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              marginBottom: 12,
              fontSize: 15,
            }}
          />
          {!canUseMasterReset && nextMasterResetAfter && (
            <p style={{ fontSize: 13, color: '#b45309', marginBottom: 12 }}>
              Master reset is on cooldown until {new Date(nextMasterResetAfter).toLocaleString()}.
            </p>
          )}
          <button
            type="submit"
            disabled={busy || !canUseMasterReset || !masterPassword || newFeesPassword.length < 8}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 8,
              border: 'none',
              background: canUseMasterReset ? '#15803d' : '#94a3b8',
              color: '#fff',
              fontWeight: 600,
              cursor: busy || !canUseMasterReset ? 'not-allowed' : 'pointer',
              marginBottom: 10,
            }}
          >
            {busy ? 'Saving…' : hasFeesPassword ? 'Reset fees password' : 'Create fees password'}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowMasterFlow(false);
              setFormError(null);
            }}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: 8,
              border: 'none',
              background: 'transparent',
              color: '#64748b',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Back
          </button>
        </form>
      )}

      {formError && (
        <p style={{ marginTop: 16, marginBottom: 0, fontSize: 14, color: '#b91c1c' }}>{formError}</p>
      )}
    </div>
  );
};
